import React, { useEffect, useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/OAuthPage.css';  // Import styles
import config from '../config.js';

import { useTheme } from '../ThemeContext.jsx';
import { fetchWrapper } from '../_helpers/fetchWrapper.js';
import { setSecret } from '../_helpers/secretStorage.js';
import {
  clearOAuthFlowState,
  isConsumedCode,
  isFlowInFlight,
  isOAuthCallbackPath,
  loginErrorCode,
  rememberConsumedCode,
  replayedCallbackDestination,
} from '../_helpers/oauthFlowState.js';
import { POST_LOGIN_REDIRECT_KEY, sanitizeReturnPath } from '../_helpers/safeRedirect.js';
import { readSession } from '../_helpers/session.js';
import { clearSessionStorage, isBetaWaitlisted, waitlistPath, waitlistStatusOf } from '../_helpers/betaWaitlist.js';
import { useT } from '../contexts/LocaleContext.jsx';
import Logo from './Logo.jsx';
import FiveDotLoader from './partial/FiveDotLoader.jsx';

const { apiBaseUrl } = config;

// Providers whose login is discontinued, with the catalog key of the display
// name shown in the "no longer supported" message.
const DISCONTINUED_PROVIDER_NAME_KEYS = {
  spotify: 'oauth.providers.spotify',
};
const DISCONTINUED_PROVIDERS = Object.keys(DISCONTINUED_PROVIDER_NAME_KEYS);

// The parameters an OAuth redirect is defined to bring back, and the only ones
// either exchange forwards. The query string is read out of the address bar,
// so whatever is in it was put there by the provider or by whoever wrote the
// link — this page cannot tell the two apart. Copying the names it expects is
// what keeps that difference from mattering; stripping the names it happens to
// know are dangerous (`verifier`, say, which no provider sends and which the
// login exchange has none of) would only cover the ones already thought of.
const CALLBACK_PARAM_NAMES = ['code', 'state', 'error', 'error_description'];

function callbackParams(queryParams) {
  const params = new URLSearchParams();
  CALLBACK_PARAM_NAMES.forEach((name) => {
    if (queryParams.has(name)) params.append(name, queryParams.get(name));
  });
  return params;
}

function OAuthPage() {
    const navigate = useNavigate();
    const t = useT();
    const { setTheme } = useTheme();
    const hasFetched = useRef(false);
    // Provider id (e.g. 'spotify') whose discontinued login was attempted; the
    // message itself is translated at render so it follows the locale.
    const [discontinuedProvider, setDiscontinuedProvider] = useState(null);

  // Every exit from this page replaces the history entry the provider created.
  // That entry's URL still carries the one-time authorization code, so leaving
  // it in the back stack is what made a spent callback replayable: Back from
  // /home returned here, found the flow state gone and ejected the user
  // (ENG-406).
  const leaveCallback = useCallback((to) => {
    navigate(to, { replace: true });
  }, [navigate]);

  const fetchCallback = useCallback(async () => {
    // Extract query parameters from the current URL
    const queryParams = new URLSearchParams(window.location.search);

    // Whether this browser was already signed in when the callback opened,
    // read before the exchange can write a JWT of its own (ENG-406).
    const wasSignedIn = readSession();

    // Reaching the callback with no login in flight means there is nothing
    // left to exchange: the URL was replayed out of history, or the flow state
    // that backed it is gone (browser storage cleared between the hand-off and
    // the return).
    // Either way the visit says nothing about the session the browser already
    // holds, so a signed-in user is put back into the app instead of onto
    // /login with an authentication error they can neither understand nor act
    // on (ENG-406). A code this browser is known to have already spent is the
    // replay proper and needs no explanation; anything else is reported where
    // the user would otherwise be left on a silent page.
    const leaveReplayedCallback = () => {
      const knownReplay = isConsumedCode(queryParams.get('code'));
      console.warn(
        knownReplay
          ? 'OAuth callback replayed with a code this browser already exchanged; returning to the app'
          : 'OAuth callback reached with no login in progress; nothing to exchange'
      );
      leaveCallback(replayedCallbackDestination({
        isSignedIn: wasSignedIn,
        state: queryParams.get('state'),
        knownReplay,
      }));
    };

    // Check if this is an account linking flow
    const isLinkingMode = localStorage.getItem('linking-mode') === 'true';

    // The linking provider is only meaningful while a linking flow is actually
    // in progress. A `linking-provider` left behind by an abandoned linking
    // attempt must not override the provider the user just chose on LoginPage,
    // or the callback goes to the wrong provider and fails with HTTP 400.
    const provider = (isLinkingMode && localStorage.getItem('linking-provider')) || localStorage.getItem('oauth-provider');

    if (provider && DISCONTINUED_PROVIDERS.includes(provider)) {
      clearOAuthFlowState();
      setDiscontinuedProvider(provider);
      return;
    }

    // A stored provider, stamped with when the browser left, is what says a
    // round-trip of this browser's own is in flight: LoginPage.handleLogin and
    // ProfilePage.handleLinkAccount write both at the moment the user hands
    // off, and the callback that consumes them clears them again.
    //
    // Neither is ever recovered from the callback URL (a `provider` query
    // param, or sniffing `iss`/`scope`): flow state a link can supply is
    // somebody else's being fed in, and honouring it would let a crafted
    // callback push its own authorization code through the exchange and sign
    // the visitor in as whoever minted that code. The age is what closes the
    // other half of that door — a user who abandons the flow at the consent
    // screen never comes back to clear the provider, and a marker left in
    // localStorage outlives the tab, so without it the same crafted link
    // works whenever such a leftover exists (FLOW_MAX_AGE_MS).
    if (!provider || !isFlowInFlight()) {
      leaveReplayedCallback();
      return;
    }

    // Whether this code has been sent before, asked before this run records
    // its own attempt. A code is single-use, so a second arrival of one is a
    // replay — the difference between "your login failed" and "you pressed
    // Back", which is the whole of ENG-406. Storing the code in secret storage
    // waits for this point too, so a replayed URL cannot put a spent code back
    // there.
    const code = queryParams.get('code');
    const isReplayedCode = isConsumedCode(code);
    setSecret('code', code);

    // The OAuth state and PKCE pair of a link are the backend's, as a login's
    // are (ENG-472): /auth/{provider}/link/initiate mints a ticket, the login
    // handler redeems it and stores the pending link — state, verifier and
    // the linking user — server-side, and the callback exchange below is held
    // to that session. This page never sees them, so the hand-off check above
    // is what this end has, for a link exactly as for a login.
    if (isLinkingMode) {
      // For account linking, call the link callback endpoint. Nothing else is
      // consulted here: a `redirect` key in localStorage is a leftover from
      // older builds (import modal, earlier linking flow) that used to divert
      // logins away from the callback exchange; it is only ever cleared now.
      try {
        const linkParams = callbackParams(queryParams);

        // Use the new callback endpoint structure for linking
        rememberConsumedCode(code);
        const response = await fetchWrapper.get(`${apiBaseUrl}/auth/${provider}/callback?${linkParams.toString()}`);

        // The linking round-trip is over; drop its one-time state
        clearOAuthFlowState();

        // Handle JSON response from backend
        if (response.success) {
          console.log('Account linked successfully');
          // Use backend-provided redirect URL or construct success URL
          const redirectUrl = response.redirectUrl || `/profile?linked=success&provider=${provider}`;
          leaveCallback(redirectUrl);
        } else {
          // Handle linking failure
          console.error('Failed to link account:', response.error || response.message);
          const redirectUrl = response.redirectUrl || `/profile?linked=error&error=${encodeURIComponent(response.error || response.message || 'Failed to link account')}`;
          leaveCallback(redirectUrl);
        }
        return;
      } catch (linkError) {
        console.error('Failed to link account:', linkError);
        clearOAuthFlowState();

        // A code this browser already sent cannot be exchanged a second time,
        // so this rejection is the replay answering, not the linking attempt
        // failing — that one already finished. Telling the user their linking
        // failed because they pressed Back would be a lie (ENG-406).
        if (isReplayedCode) {
          console.warn('Ignoring a replayed account-linking callback; its exchange already happened');
          leaveCallback('/profile');
          return;
        }

        // Extract error message for better user feedback
        let errorMessage = 'Failed to link account';
        if (linkError.response) {
          errorMessage = linkError.response;
        } else if (linkError.message) {
          errorMessage = linkError.message;
        }

        leaveCallback(`/profile?linked=error&error=${encodeURIComponent(errorMessage)}`);
        return;
      }
    }

    try {
      // Recorded as it goes out, so the next arrival of this code — a reload,
      // a Back press, a restored tab — is read as the replay it is
      rememberConsumedCode(code);
      const authData = await fetchWrapper.get(`${apiBaseUrl}/auth/${provider}/callback?${callbackParams(queryParams).toString()}`, undefined, false);

      // The stored provider was single-use; clear it, and the rest of the flow
      // state with it, so nothing stale is carried into the next login attempt
      clearOAuthFlowState();

      // Save the JWT token in localStorage
      sessionStorage.setItem('jwt', authData.token);

      //set the userId from the jwt token
      const jwt = authData.token
      const base64Url = jwt.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const payload = JSON.parse(window.atob(base64));
      const userId = payload.user_id;
      sessionStorage.setItem('userId', userId);

      try {
        const profileData = await fetchWrapper.get(`${apiBaseUrl}/user/profile/${userId}`);
        
        // Save the user's profile image URL in sessionStorage. Without one the
        // key is removed: setItem(key, null) would store the string "null",
        // which reads as a picture (ENG-764).
        const profileImageURL = profileData.profile_image_url;
        if (typeof profileImageURL === 'string' && profileImageURL.trim() !== '') {
          sessionStorage.setItem('ProfileImageURL', profileImageURL);
        } else {
          sessionStorage.removeItem('ProfileImageURL');
        }

        //gets the theme from the data object and triggers a theme change
        sessionStorage.setItem('theme', profileData.theme);
        const theme = profileData.theme || config.defaultTheme;
        setTheme(theme);

        // Return the user to the page they were on before their session
        // expired — unless that page is this callback, whose code is spent by
        // the time we would go back to it. Builds before ENG-406 could store
        // `/oauth?code=…` here, and following it lands the freshly signed-in
        // user on the login error page.
        // Only a same-origin path is followed (ENG-499): a crafted value
        // like `//evil.com` would otherwise make the login an open redirect.
        const postLoginRedirect = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
        if (postLoginRedirect) {
          sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
          const returnPath = sanitizeReturnPath(postLoginRedirect);
          if (returnPath && !isOAuthCallbackPath(returnPath)) {
            leaveCallback(returnPath);
            return;
          }
        }

        // Redirect to the users default page (home if unset)
        if (profileData.default_login_page !== undefined && profileData.default_login_page !== null && profileData.default_login_page !== '') {
          leaveCallback(profileData.default_login_page);
        } else {
          leaveCallback('/home');
        }

      } catch (profileError) {
        console.error('Failed to fetch user profile:', profileError);
        // Still try to navigate to prevent user from being stuck
        leaveCallback('/home');
      }

    } catch (error) {
        console.error('Failed to complete OAuth callback:', error);

        // A failed callback must not leave its flow state behind for the
        // retry that LoginPage is about to start
        clearOAuthFlowState();

        // The closed beta's gate (ENG-913) refused this account: it is on the
        // waitlist, not failing to sign in. No token came back, and none from
        // an earlier session is kept either, so the browser leaves holding
        // nothing (ENG-915).
        if (isBetaWaitlisted(error)) {
          clearSessionStorage();
          leaveCallback(waitlistPath(waitlistStatusOf(error), provider));
          return;
        }

        // This browser already sent this code once, so the backend is
        // refusing a spent code rather than reporting anything about this
        // visit — a reload that re-ran the callback, or a replay that still
        // had flow state behind it. Nothing has gone wrong that the user did
        // not already live through, so they are put back where they belong
        // rather than shown an authentication error (ENG-406).
        //
        // The test is the code, not the session: a signed-in user whose fresh
        // login genuinely fails, and a user whose JWT is present but expired
        // server-side, both reach here with a code that was never spent, and
        // both are told what happened.
        if (isReplayedCode) {
          console.warn('Ignoring a failed re-exchange of a spent OAuth callback');
          leaveCallback(replayedCallbackDestination({
            isSignedIn: wasSignedIn,
            state: queryParams.get('state'),
            knownReplay: true,
          }));
          return;
        }

        // A code LoginPage words, not the server's text (ENG-515): that text
        // was put in the URL and never matched a message, so every failure
        // read "An error occurred."
        leaveCallback(`/login?error=${loginErrorCode(error, queryParams.get('error'))}`);
    }
  }, [leaveCallback, setTheme]);

  useEffect(() => {
    if (!hasFetched.current) {
      hasFetched.current = true;
      fetchCallback();
    }
  }, [fetchCallback]);

  if (discontinuedProvider) {
    return (
      <div className="oauth-page" data-theme="songshake">
        <div className="oauth-page-content">
          <Logo linkToHome={false} />
          <h1 className="oauth-page-title">{t('oauth.unavailable.title')}</h1>
          <p className="oauth-page-description">
            {t('oauth.unavailable.discontinuedProvider', { provider: t(DISCONTINUED_PROVIDER_NAME_KEYS[discontinuedProvider]) })}
          </p>
          <button className="oauth-page-btn-back" onClick={() => leaveCallback('/login')}>{t('oauth.unavailable.backToLogin')}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="oauth-page" data-theme="songshake">
      <div className="oauth-page-content">
        <Logo linkToHome={false} />
        <FiveDotLoader ariaLabel={t('oauth.authenticating.loaderLabel')} />
        <h1 className="oauth-page-title">{t('oauth.authenticating.title')}</h1>
        <p className="oauth-page-description">{t('oauth.authenticating.description')}</p>
      </div>
    </div>
  );
}

export default OAuthPage;